import { ExecutionData } from "../../../../../rule-engine-core/Common/Data/ExecutionData/ExecutionData";
import { TextMergeConfiguration } from "../../../../../rule-engine-core/TextMerge/TextMergeConfiguration";
import type {
    DemoRuleIntegrationAdapter,
    RuleConfigFactoryContext,
    RuleEvaluatorPort,
    RuleIntegrationStatus,
    RulePolicyBinding,
    RuleRowMapperPort,
    RuleSummaryPresenterPort,
} from "../../ruleIntegrationTypes";
import { identityRuleRowMapper } from "../../ruleIntegrationTypes";
import { withExecutableRuntimeEvidence } from "../runtimeEvidence";
import { textMergeStorageCodec } from "../storageCodecs";

export const TEXT_MERGE_EXECUTABLE_TYPE = "TextMergeConfiguration";

export type CreateTextMergeIntegrationAdapterParams<DemoRow, RuntimeRow = DemoRow> = {
    ruleId: string;
    status?: Exclude<RuleIntegrationStatus, "demo_only">;
    policyBinding?: RulePolicyBinding;
    createConfig: (context?: RuleConfigFactoryContext) => TextMergeConfiguration;
    rowMapper?: RuleRowMapperPort<DemoRow, RuntimeRow>;
    evaluator: RuleEvaluatorPort<TextMergeConfiguration, RuntimeRow>;
    summaryPresenter: RuleSummaryPresenterPort<TextMergeConfiguration>;
};

export const createTextMergeIntegrationAdapter = <DemoRow, RuntimeRow = DemoRow>({
    ruleId,
    status = "native_appglue",
    policyBinding,
    createConfig,
    rowMapper,
    evaluator,
    summaryPresenter,
}: CreateTextMergeIntegrationAdapterParams<DemoRow, RuntimeRow>): DemoRuleIntegrationAdapter<
    TextMergeConfiguration,
    DemoRow,
    RuntimeRow
> => ({
    ruleId,
    status,
    appGlueExecutableType: TEXT_MERGE_EXECUTABLE_TYPE,
    editorSurface: "native_text_merge_builder",
    policyBinding,
    ports: {
        configFactory: { createConfig },
        storageCodec: textMergeStorageCodec(),
        rowMapper: rowMapper ?? (identityRuleRowMapper<DemoRow>() as unknown as RuleRowMapperPort<DemoRow, RuntimeRow>),
        evaluator: withExecutableRuntimeEvidence(TEXT_MERGE_EXECUTABLE_TYPE, evaluator),
        summaryPresenter,
    },
});

export const runTextMergeToPlainText = (config: TextMergeConfiguration, data: ExecutionData): string => {
    const rawOutput = config.run(data, undefined);
    return String(rawOutput ?? "")
        .replace(/<[^>]*>/g, " ")
        .replace(/&nbsp;/g, " ")
        .replace(/\s+/g, " ")
        .replace(/\s+([,.;:])/g, "$1")
        .trim();
};
